import React, { useState, useCallback } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import { Sparkles, Loader2, Copy, Check, RefreshCw, X } from "lucide-react";

export function AIButton({ onClick, loading, disabled, label = "AI Assist", testid, className = "" }) {
  return (
    <button
      type="button"
      data-testid={testid}
      onClick={onClick}
      disabled={loading || disabled}
      className={`inline-flex items-center gap-1.5 rounded-full border border-emerald-200 bg-emerald-50 px-3 py-1.5 text-xs font-semibold text-emerald-700 transition-colors hover:bg-emerald-100 disabled:opacity-50 ${className}`}
    >
      {loading ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Sparkles className="h-3.5 w-3.5" />}
      {label}
    </button>
  );
}

export function CopyButton({ text, testid, label = "Copy" }) {
  const [copied, setCopied] = useState(false);
  const copy = async () => {
    try {
      await navigator.clipboard.writeText(text);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch {
      toast.error("Could not copy");
    }
  };
  return (
    <button
      type="button"
      data-testid={testid}
      onClick={copy}
      className="inline-flex items-center gap-1 rounded-full border border-zinc-200 bg-white px-2.5 py-1 text-xs font-medium text-zinc-600 transition-colors hover:bg-zinc-50"
    >
      {copied ? <Check className="h-3 w-3 text-emerald-600" /> : <Copy className="h-3 w-3" />}
      {copied ? "Copied" : label}
    </button>
  );
}

/** Shell around a set of AI suggestions with regenerate/dismiss controls. */
export function AIPanel({ title, onRegenerate, onDismiss, loading, children, testid }) {
  return (
    <div data-testid={testid} className="mt-3 rounded-2xl border border-emerald-200 bg-emerald-50/60 p-4">
      <div className="mb-2 flex items-center gap-2">
        <Sparkles className="h-4 w-4 text-emerald-600" />
        <span className="font-display text-sm font-bold text-emerald-800">{title}</span>
        <div className="ml-auto flex items-center gap-1.5">
          {onRegenerate && (
            <button type="button" data-testid="ai-regenerate" onClick={onRegenerate} disabled={loading}
              className="inline-flex items-center gap-1 rounded-full px-2 py-1 text-xs font-medium text-emerald-700 hover:bg-emerald-100 disabled:opacity-50">
              {loading ? <Loader2 className="h-3 w-3 animate-spin" /> : <RefreshCw className="h-3 w-3" />} Regenerate
            </button>
          )}
          {onDismiss && (
            <button type="button" data-testid="ai-dismiss" onClick={onDismiss}
              className="inline-flex items-center gap-1 rounded-full px-2 py-1 text-xs font-medium text-zinc-500 hover:bg-zinc-100">
              <X className="h-3 w-3" /> Dismiss
            </button>
          )}
        </div>
      </div>
      <p className="mb-3 text-[11px] text-emerald-700/80">AI suggestions — review and edit before using. Keep your own voice.</p>
      {children}
    </div>
  );
}

/** Handles the API call + loading + disabled/limit handling for one endpoint. */
export function useAIAssist(path) {
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [action, setAction] = useState(null);

  const run = useCallback(async (body) => {
    setLoading(true);
    try {
      const r = await api.post(path, body);
      setResult(r.data.result);
      setAction(body.action);
      return r.data.result;
    } catch (e) {
      const status = e.response?.status;
      if (status === 503) toast.error("AI Assist is temporarily unavailable.");
      else if (status === 429) toast.error(formatApiErrorDetail(e.response?.data?.detail));
      else toast.error(formatApiErrorDetail(e.response?.data?.detail) || "AI request failed");
      return null;
    } finally {
      setLoading(false);
    }
  }, [path]);

  const reset = useCallback(() => { setResult(null); setAction(null); }, []);
  return { loading, result, action, run, reset };
}
